// SecureEmbed Academic Discovery Portal - Branded Header
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Menu, BookOpen, ChevronDown, Settings, LogOut } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useBranding } from '../../contexts/BrandingContext'
import { useRole } from '../../contexts/RoleContext'
import { ROLE_LABELS } from '../../constants/roles'

interface BrandedHeaderProps {
  onMenuToggle?: () => void
}

export default function BrandedHeader({ onMenuToggle }: BrandedHeaderProps) {
  const { user, logout } = useAuth()
  const { branding } = useBranding()
  const { role } = useRole()
  const navigate = useNavigate()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const institutionName = branding?.institution_name || 'Academic Discovery Portal'
  const logoUrl = branding?.logo_url

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function handleLogout() {
    setDropdownOpen(false)
    logout()
    navigate('/login')
  }

  function handleSettings() {
    setDropdownOpen(false)
    navigate('/settings')
  }

  return (
    <header
      className="bg-white border-b border-gray-200 px-4 md:px-6 h-16 flex items-center justify-between"
      style={{ borderBottomColor: 'var(--brand-primary, #EA6025)' }}
    >
      {/* Left: hamburger + branding */}
      <div className="flex items-center gap-3">
        {/* Mobile hamburger */}
        {onMenuToggle && (
          <button
            onClick={onMenuToggle}
            className="md:hidden p-1.5 text-gray-600 hover:text-gray-800 rounded"
            aria-label="Toggle navigation"
          >
            <Menu size={22} />
          </button>
        )}

        {/* Logo / icon */}
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={institutionName}
            className="h-8 w-8 object-contain rounded"
          />
        ) : (
          <div
            className="h-8 w-8 rounded flex items-center justify-center"
            style={{ backgroundColor: 'var(--brand-primary, #EA6025)' }}
          >
            <BookOpen size={18} className="text-white" />
          </div>
        )}

        {/* Institution name - hidden on small screens */}
        <h1
          className="hidden sm:block text-lg font-bold truncate max-w-xs"
          style={{ color: 'var(--brand-primary, #EA6025)' }}
        >
          {institutionName}
        </h1>
      </div>

      {/* Right: user profile dropdown */}
      {user && (
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div
              className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium text-white"
              style={{ backgroundColor: 'var(--brand-primary, #EA6025)' }}
            >
              {user.email.charAt(0).toUpperCase()}
            </div>
            <span className="hidden sm:block text-sm text-gray-700 max-w-[160px] truncate">
              {user.email}
            </span>
            <ChevronDown size={14} className="text-gray-500" />
          </button>

          {/* Dropdown menu */}
          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              {/* User info */}
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-800 truncate">{user.email}</p>
                {role && (
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-acad-50 text-acad-700">
                    {ROLE_LABELS[role] || role}
                  </span>
                )}
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={handleSettings}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Settings size={16} />
                  <span>Settings</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  )
}
